/**
 * AM MM selection summary: add-to-cart via mix-and-match-cart (line item properties).
 */

import { addMixAndMatchToCart } from '@theme/mix-and-match-cart';

/**
 * @typedef {{ handle: string, quantity: number }} SummarySelectionEntry
 * @typedef {{ handle?: string, title?: string, price?: number | string, image?: string, variant_id?: number | null, product_url?: string }} SummaryProduct
 */

/**
 * @param {string | null} raw
 * @returns {SummarySelectionEntry[]}
 */
function parseSelection(raw) {
  try {
    const parsed = JSON.parse(raw || '[]');
    if (!Array.isArray(parsed)) return [];
    /** @type {SummarySelectionEntry[]} */
    const normalized = [];
    parsed.forEach((entry) => {
      if (typeof entry === 'string') {
        if (entry) normalized.push({ handle: entry, quantity: 1 });
        return;
      }
      if (!entry || typeof entry !== 'object') return;
      const candidate = /** @type {{ handle?: unknown, quantity?: unknown }} */ (entry);
      const handle = String(candidate.handle || '');
      const quantity = Number(candidate.quantity || 0);
      if (!handle) return;
      normalized.push({ handle, quantity: quantity > 0 ? quantity : 1 });
    });
    return normalized;
  } catch {
    return [];
  }
}

/**
 * @param {HTMLElement | null} root
 */
function initSummary(root) {
  if (!root || root.dataset.mmSummaryAtcInit === 'true') return;
  root.dataset.mmSummaryAtcInit = 'true';

  const key = root.getAttribute('data-selection-key') || 'am_mm_selection';
  const productsEl = root.querySelector('[data-mm-summary-products]');
  const ctaEl = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-mm-summary-cta]'));
  const messageEl = root.querySelector('[data-mm-summary-message]');
  const addToCartErrorText = root.getAttribute('data-error-add-to-cart') || 'Could not add to cart.';

  /** @type {Record<string, SummaryProduct>} */
  let productsMap = {};
  try {
    const list = JSON.parse((productsEl && productsEl.textContent) || '[]');
    /** @type {SummaryProduct[]} */ ((list || [])).forEach((item) => {
      if (item && item.handle) productsMap[item.handle] = item;
    });
  } catch {}

  if (!ctaEl) return;

  ctaEl.addEventListener('click', async () => {
    if (ctaEl.disabled) return;

    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    const selected = parseSelection(raw);

    const children = [];
    let setPriceCents = 0;
    for (const entry of selected) {
      const item = productsMap[entry.handle];
      if (!item || item.variant_id == null) continue;
      const qty = Number(entry.quantity) || 1;
      const priceCents = Number(item.price) || 0;
      children.push({
        variantId: item.variant_id,
        quantity: qty,
        title: item.title || '',
        imageUrl: item.image || '',
        productUrl: item.product_url || '',
        priceCents,
      });
      setPriceCents += priceCents * qty;
    }

    if (children.length === 0) return;

    const state = {
      children,
      setPriceCents,
      shellTitle: root.getAttribute('data-shell-title') || 'Mix & Match',
      shellImage: root.getAttribute('data-shell-image') || '',
      shellUrl: root.getAttribute('data-shell-url') || '',
      productId: root.getAttribute('data-shell-product-id') || '',
      mixAndMatchType: root.getAttribute('data-mm-mix-match-type') || root.getAttribute('data-mm-summary-discount-rule-type') || 'main',
    };

    ctaEl.disabled = true;
    root.classList.add('am-mm-summary--adding');
    if (messageEl) messageEl.textContent = '';

    try {
      const result = await addMixAndMatchToCart(state);

      if (result.ok) {
        try {
          localStorage.setItem(key, '[]');
        } catch {}
        window.dispatchEvent(new CustomEvent('am:mm:selection:changed'));
        const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (document.querySelector('cart-drawer-component'));
        if (drawer && typeof drawer.open === 'function') drawer.open();
      } else {
        if (messageEl) messageEl.textContent = result.error || addToCartErrorText;
      }
    } catch (err) {
      if (messageEl) messageEl.textContent = (err && err.message) ? err.message : addToCartErrorText;
    } finally {
      root.classList.remove('am-mm-summary--adding');
      ctaEl.disabled = false;
    }
  });
}

function initAll() {
  document.querySelectorAll('[data-am-mm-summary]').forEach((el) => initSummary(/** @type {HTMLElement} */ (el)));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
document.addEventListener('shopify:section:load', initAll);
